import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import MuiCard from '@mui/material/Card';
import {styled} from '@mui/material/styles';
import AppTheme from './shared-theme/AppTheme';
import {SitemarkIcon} from './components/CustomIcons';
import {useNavigate,useSearchParams} from "react-router";
import {useEffect,useState} from 'react';
import {useResetPasswordMutation} from './redux/authApi';

const md5=require('md5');


const Card=styled(MuiCard)(({theme})=>(
	{
	display:'flex',
	flexDirection:'column',
	alignSelf:'center',
	width:'100%',
	padding:theme.spacing(4),
	gap:theme.spacing(2),
	margin:'auto',

	boxShadow:
		'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',

	[theme.breakpoints.up('sm')]:
		{
		width:'450px',
		},

	...theme.applyStyles('dark',
		{
		boxShadow:
			'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
		}),
	}));


const SignUpContainer=styled(Stack)(({theme})=>(
	{
	height:'calc((1 - var(--template-frame-height, 0)) * 100dvh)',
	minHeight:'100%',
	padding:theme.spacing(2),

	[theme.breakpoints.up('sm')]:
		{
		padding:theme.spacing(4),
		},

	'&::before':
		{
		content:'""',
		display:'block',
		position:'absolute',
		zIndex:-1,
		inset:0,
		backgroundImage:
			'radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))',
		backgroundRepeat:'no-repeat',

		...theme.applyStyles('dark',
			{
			backgroundImage:
				'radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))',
			}),
		},
	}));


export default function ResetPassword(props)
	{
	const [searchParams]=useSearchParams();
	const navigate=useNavigate();

	const [resetPassword,result]=useResetPasswordMutation();

	const [passwordError,setPasswordError]=useState(false);
	const [passwordErrorMessage,setPasswordErrorMessage]=useState('');

	const [currentPasswordError,setCurrentPasswordError]=useState(false);
	const [currentPasswordErrorMessage,setCurrentPasswordErrorMessage]=useState('');

	const [accessToken,setAccessToken]=useState('');
	const [email,setEmail]=useState('');


	useEffect(()=>
		{
		for (const entry of searchParams.entries())
			{
			if (entry[0]==='email')
				setEmail(entry[1]);
			else if (entry[0]==='access')
				setAccessToken(entry[1]);
			}
		},[searchParams]);


	useEffect(()=>
		{
		if (!result.isSuccess)
			return;

		console.log(result.data);

		if (result.data?.success)
			{
			navigate("/");
			return;
			}

		setPasswordErrorMessage(
			result.data?.failure ?? 'Unable to change password.'
			);

		setPasswordError(true);
		},[result,navigate]);


	const validateInputs=()=>
		{
		const password=document.getElementById('password')?.value ?? '';
		const verifyPassword=document.getElementById('verifypassword')?.value ?? '';
		const currentPassword=document.getElementById('currentPassword')?.value ?? '';

		let isValid=true;


		setPasswordError(false);
		setPasswordErrorMessage('');

		setCurrentPasswordError(false);
		setCurrentPasswordErrorMessage('');


		if (accessToken==='' && !currentPassword)
			{
			setCurrentPasswordError(true);
			setCurrentPasswordErrorMessage('Current password is required.');
			isValid=false;
			}


		if (!password || password.length<6)
			{
			setPasswordError(true);
			setPasswordErrorMessage(
				'Password must be at least 6 characters long.'
				);

			isValid=false;
			}
		else if (password!==verifyPassword)
			{
			setPasswordError(true);
			setPasswordErrorMessage('Passwords do not match.');

			isValid=false;
			}


		return isValid;
		};


	const handleSubmit=(event)=>
		{
		event.preventDefault();

		if (!validateInputs())
			return;


		const password=document.getElementById('password').value;
		const currentPassword=
			document.getElementById('currentPassword')?.value ?? '';


		const obj=
			{
			accessToken:md5(accessToken),
			email,
			password:md5(password),
			currentPassword:md5(currentPassword)
			};


		console.log(obj);

		resetPassword(obj);
		};


	return (
		<AppTheme {...props}>
			<CssBaseline enableColorScheme />

			<SignUpContainer
				direction="column"
				justifyContent="space-between"
			>
				<Card variant="outlined">

					<SitemarkIcon />

					<Typography
						component="h1"
						variant="h4"
						sx={{
							width:'100%',
							fontSize:'clamp(2rem, 10vw, 2.15rem)'
							}}
					>
						Change Password
					</Typography>


					<Box
						component="form"
						onSubmit={handleSubmit}
						sx={{
							display:'flex',
							flexDirection:'column',
							gap:2
							}}
					>

						{accessToken==='' &&
							<FormControl>

								<FormLabel htmlFor="currentPassword">
									Current Password
								</FormLabel>

								<TextField
									required
									fullWidth
									name="currentPassword"
									placeholder="••••••"
									type="password"
									id="currentPassword"
									autoComplete="current-password"
									variant="outlined"
									error={currentPasswordError}
									helperText={currentPasswordErrorMessage}
									color={
										currentPasswordError
											?'error'
											:'primary'
										}
								/>

							</FormControl>
						}


						<FormControl>

							<FormLabel htmlFor="password">
								Password
							</FormLabel>

							<TextField
								required
								fullWidth
								name="password"
								placeholder="••••••"
								type="password"
								id="password"
								autoComplete="new-password"
								variant="outlined"
								error={passwordError}
								helperText={passwordErrorMessage}
								color={
									passwordError
										?'error'
										:'primary'
									}
							/>

						</FormControl>


						<FormControl>

							<FormLabel htmlFor="verifypassword">
								Verify Password
							</FormLabel>

							<TextField
								required
								fullWidth
								name="verifypassword"
								placeholder="••••••"
								type="password"
								id="verifypassword"
								autoComplete="new-password"
								variant="outlined"
								error={passwordError}
								color={
									passwordError
										?'error'
										:'primary'
									}
							/>

						</FormControl>


						<Button
							type="submit"
							fullWidth
							variant="contained"
							disabled={result.isLoading}
						>
							{result.isLoading
								?'Changing Password...'
								:'Change Password'
							}
						</Button>

					</Box>

				</Card>
			</SignUpContainer>
		</AppTheme>
		);
	}